import {Table} from 'platform/components';
import {Box, HStack, Text, VStack} from 'platform/foundation';

import {useState} from 'react';

import {
  FinancingRateMatrixCell,
  SaveSaleVehicleFinancingBody,
  VehicleFinancingType,
} from '@dms/api/vehicle';
import {RATE_MATRIX_DOWN_PAYMENTS} from '@dms/features/vehicle-financing-template';
import i18n from '@dms/i18n';

import {FinancingMatrixBulkActions} from './FinancingMatrixBulkActions';
import {FinancingMatrixRow} from './FinancingMatrixRow';
import {MatrixLegendItem} from './MatrixLegendItem';
import {FinancingCellCoordinates} from './types/FinancingCellCoordinates';
import {FinancingCellRates} from './types/FinancingCellRates';
import {VehicleFinancingPricing} from './types/VehicleFinancingPricing';
import {getFinancingCellKey} from './utils/financingCells/getFinancingCellKey';
import {toggleFinancingCellSelection} from './utils/financingCells/toggleFinancingCellSelection';

interface FinancingMatrixProps {
  vehicleFinancingBody: SaveSaleVehicleFinancingBody;
  type: VehicleFinancingType;
  balloonPayment: number | null;
  vehiclePrice: number;
  currency: string;
  pricing: VehicleFinancingPricing | null;
  templateCells: FinancingRateMatrixCell[];
  isTemplatePending?: boolean;
  isReadOnly: boolean;
  onBulkApply?: (selection: FinancingCellCoordinates[], rates: FinancingCellRates) => void;
  onBulkDelete?: (selection: FinancingCellCoordinates[]) => void;
  onEditCell: (cell: FinancingCellCoordinates) => void;
}

export function FinancingMatrix(props: FinancingMatrixProps) {
  // Selection lives here (not in the panel) so shift-clicking cells re-renders only the matrix.
  // The panel resets it through the `key` prop when the surrounding draft context changes.
  const [selectedCoordinates, setSelectedCoordinates] = useState<FinancingCellCoordinates[]>([]);
  const bracketSelection = selectedCoordinates.filter(
    (coordinates) => coordinates.balloonPayment === props.balloonPayment
  );
  const clearBracketSelection = () =>
    setSelectedCoordinates((selection) =>
      selection.filter((coordinates) => coordinates.balloonPayment !== props.balloonPayment)
    );
  // Coordinates are unique inside a rate matrix, so one keyed lookup per collection replaces the
  // linear scans the grid would otherwise run for every one of its positions.
  const cellsByCoordinateKey = new Map(
    props.vehicleFinancingBody.rateMatrix.cells
      .filter((cell) => cell.balloonPayment === props.balloonPayment)
      .map((cell): [string, FinancingRateMatrixCell] => [getFinancingCellKey(cell), cell])
  );
  const templateCellsByCoordinateKey = new Map(
    props.templateCells.map((cell): [string, FinancingRateMatrixCell] => [
      getFinancingCellKey(cell),
      cell,
    ])
  );
  const selectedCellKeys = new Set(bracketSelection.map(getFinancingCellKey));

  return (
    <VStack spacing={4}>
      <FinancingMatrixBulkActions
        selectedCount={bracketSelection.length}
        onClear={clearBracketSelection}
        onApply={(rates) => {
          props.onBulkApply?.(bracketSelection, rates);
          clearBracketSelection();
        }}
        onDelete={() => {
          props.onBulkDelete?.(bracketSelection);
          clearBracketSelection();
        }}
      />
      <Box overflowX="auto">
        <Box width="100%" minWidth={280}>
          <Table
            variant="plain"
            tableLayout="fixed"
            columns={[
              {
                width: 20,
                element: (
                  <Text size="xSmall" color="secondary">
                    {i18n.t('page.vehicle.financingAndLeasing.term')}
                  </Text>
                ),
              },
              ...RATE_MATRIX_DOWN_PAYMENTS.map((downPayment) => ({
                width: 26 as const,
                element: (
                  <Text size="xSmall" color="secondary">
                    {`${downPayment} %`}
                  </Text>
                ),
              })),
            ]}
          >
            {props.vehicleFinancingBody.rateMatrix.availableTerms.map((term) => (
              <FinancingMatrixRow
                key={term}
                vehicleFinancingBody={props.vehicleFinancingBody}
                type={props.type}
                term={term}
                balloonPayment={props.balloonPayment}
                cellsByCoordinateKey={cellsByCoordinateKey}
                templateCellsByCoordinateKey={templateCellsByCoordinateKey}
                selectedCellKeys={selectedCellKeys}
                vehiclePrice={props.vehiclePrice}
                currency={props.currency}
                pricing={props.pricing}
                isTemplatePending={props.isTemplatePending}
                isReadOnly={props.isReadOnly}
                onToggleSelection={(coordinates) =>
                  setSelectedCoordinates(
                    toggleFinancingCellSelection(selectedCoordinates, coordinates)
                  )
                }
                onEditCell={(coordinates) => {
                  clearBracketSelection();
                  props.onEditCell(coordinates);
                }}
              />
            ))}
          </Table>
        </Box>
      </Box>
      <HStack spacing={2} wrap>
        <MatrixLegendItem
          label={i18n.t('page.vehicle.financingAndLeasing.activePrimary')}
          backgroundColor="palettes.yellow.20.100"
        />
        <MatrixLegendItem
          label={i18n.t('page.vehicle.financingAndLeasing.configured')}
          backgroundColor="palettes.blue.20.100"
        />
        <MatrixLegendItem
          label={i18n.t('page.vehicle.financingAndLeasing.manualOverride')}
          backgroundColor="palettes.purple.20.100"
        />
        <MatrixLegendItem
          label={i18n.t('page.vehicle.financingAndLeasing.emptyClickToCreate')}
          backgroundColor="palettes.neutral.20.100"
        />
        <Text size="xSmall" color="secondary">
          {i18n.t('page.vehicle.financingAndLeasing.multiSelectHint')}
        </Text>
      </HStack>
    </VStack>
  );
}
